import React, { useState } from 'react';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import { Box, Link, useTheme } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import AuthScreen from '../../components/Auth/AuthScreen';
import { authTokens, emberGradient, FONT_DISPLAY, FONT_MONO } from '../../components/Auth/brandTokens';
import { BrandMark } from '../../components/Brand/Logo';

const errorText = (code?: string) => {
  switch (code) {
    case 'invalid_or_expired_code': return 'That code is invalid or has expired — request a new one.';
    case 'too_many_attempts': return 'Too many attempts. Request a new code.';
    case 'invalid_request': return 'Enter the 6-digit code from your email.';
    default: return 'Could not verify. Please try again.';
  }
};

const VerifyEmail: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { verifyEmail, resendCode } = useAuth();
  const themeMode = useTheme().palette.mode === 'dark' ? 'dark' : 'light';
  const t = authTokens(themeMode);
  const email = (location.state as any)?.email || new URLSearchParams(location.search).get('email') || '';

  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resent, setResent] = useState(false);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(code)) { setError('Enter the 6-digit code from your email.'); return; }
    setSubmitting(true);
    setError(null);
    const res = await verifyEmail(email, code);
    setSubmitting(false);
    if (res.ok && res.alreadyVerified) navigate('/login', { state: { email } });
    else if (res.ok) navigate('/');
    else setError(errorText(res.error));
  };

  const onResend = async () => { setResent(false); setError(null); await resendCode(email); setResent(true); };

  const mono = { fontFamily: FONT_MONO, textTransform: 'uppercase' as const };

  return (
    <AuthScreen>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.25, fontFamily: "'Hanken Grotesk', sans-serif", color: t.text }}>
        <Box sx={{ width: 56, height: 56, borderRadius: 999, display: 'grid', placeItems: 'center', background: t.emberSoft, border: `1px solid ${t.line}` }}>
          <BrandMark size={26} color={t.ember} />
        </Box>
        <Box>
          <Box component="h2" sx={{ fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: 26, letterSpacing: '-0.02em', m: 0, color: t.text }}>Verify your email</Box>
          <Box component="p" sx={{ m: '6px 0 0', fontSize: 14.5, color: t.muted, lineHeight: 1.5 }}>
            {email ? <>We sent a 6-digit code to <Box component="b" sx={{ color: t.text }}>{email}</Box>. Enter it to finish.</> : 'Enter the 6-digit code we emailed you.'}
          </Box>
        </Box>

        {!email && (
          <Box sx={{ fontSize: 13, color: t.muted }}>
            Start from <Link component={RouterLink} to="/register" sx={{ color: t.ember }}>sign up</Link> or <Link component={RouterLink} to="/login" sx={{ color: t.ember }}>sign in</Link>.
          </Box>
        )}

        <Box component="form" onSubmit={onSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
            <Box component="label" htmlFor="df-code" sx={{ ...mono, fontSize: 10.5, letterSpacing: '0.13em', color: t.muted }}>6-digit code</Box>
            <Box component="input" id="df-code" inputMode="numeric" autoFocus placeholder="••••••"
              value={code} onChange={(e: any) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              sx={{ width: '100%', boxSizing: 'border-box', background: 'transparent', border: 'none', borderBottom: `1.5px solid ${error ? t.err : t.line}`, padding: '10px 2px', fontSize: 26, letterSpacing: '0.5em', textAlign: 'center', fontFamily: FONT_MONO, color: t.text, outline: 'none', '&:focus': { borderBottomColor: t.ember } }} />
          </Box>

          {error && <Box sx={{ fontSize: 12.5, color: t.err, fontWeight: 600 }}>{error}</Box>}
          {resent && <Box sx={{ fontSize: 12.5, color: t.ember, fontWeight: 600 }}>A new code is on its way.</Box>}

          <Box component="button" type="submit" disabled={submitting || code.length !== 6}
            sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '9px', width: '100%', minHeight: 52, border: 'none', borderRadius: '2px', cursor: submitting || code.length !== 6 ? 'default' : 'pointer', fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: 16, color: t.onEmber, background: emberGradient(t), boxShadow: `0 8px 24px -8px ${t.shadow}`, opacity: submitting || code.length !== 6 ? 0.65 : 1, transition: 'filter .2s' }}>
            {submitting ? 'Verifying…' : 'Verify & continue'} <Box component="span" aria-hidden sx={{ fontWeight: 700 }}>→</Box>
          </Box>

          <Box sx={{ textAlign: 'center' }}>
            <Box component="button" type="button" onClick={onResend} disabled={!email}
              sx={{ background: 'none', border: 'none', color: t.muted, cursor: email ? 'pointer' : 'default', fontSize: 13, fontFamily: "'Hanken Grotesk', sans-serif", textDecoration: 'underline' }}>
              Resend code
            </Box>
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Link component={RouterLink} to="/login" sx={{ color: t.ember, fontWeight: 700, fontSize: 14 }} underline="hover">Back to sign in</Link>
          </Box>
        </Box>
      </Box>
    </AuthScreen>
  );
};

export default VerifyEmail;
